import React, { useState } from 'react';
import { Camera, Send, X, AlertCircle } from 'lucide-react';
import imageCompression from "browser-image-compression";

interface RarePartsRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    vehicleInfo: any;
    prefillPart?: string;
    onSubmit: (data: any) => Promise<void>;
}

export const RarePartsRequestModal: React.FC<RarePartsRequestModalProps> = ({
    isOpen,
    onClose,
    vehicleInfo,
    prefillPart,
    onSubmit
}) => {
    const [partName, setPartName] = useState(prefillPart || "");
    const [oemNumber, setOemNumber] = useState("");
    const [image, setImage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const options = { maxWidthOrHeight: 800, useWebWorker: true, maxSizeMB: 0.5 };
            const compressedFile = await imageCompression(file, options);
            const reader = new FileReader();
            reader.onloadend = () => setImage(reader.result as string);
            reader.readAsDataURL(compressedFile);
        } catch (err) {
            console.error("Image processing error", err);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!partName) return;

        setLoading(true);
        try {
            await onSubmit({
                part_name: partName,
                oem_number: oemNumber,
                image_data: image,
                vehicle_info: vehicleInfo
            });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-[#1a1a1a] border border-white/10 w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-white">طلب قطعة نادرة</h2>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">
                            {vehicleInfo.make} {vehicleInfo.model} ({vehicleInfo.year})
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full text-gray-400">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 block">اسم القطعة</label>
                            <input
                                type="text"
                                value={partName}
                                onChange={(e) => setPartName(e.target.value)}
                                placeholder="مثلاً: طرمبة بنزين، حساس شكمان..."
                                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-primary/50"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase mb-2 block">رقم OEM (اختياري)</label>
                            <input
                                type="text"
                                value={oemNumber}
                                onChange={(e) => setOemNumber(e.target.value)}
                                placeholder="اذا متوفر... (مثلاً: 90919-01164)"
                                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-mono outline-none focus:border-primary/50"
                            />
                        </div>

                        <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 flex gap-3">
                            <AlertCircle className="text-blue-400 shrink-0" size={18} />
                            <p className="text-[10px] text-blue-300 leading-relaxed">
                                يفضل إرفاق صورة واضحة للقطعة أو كود الـ OEM لزيادة سرعة العثور عليها وتجنب الأخطاء.
                            </p>
                        </div>

                        <div className="flex gap-4">
                            <label className="flex-1 cursor-pointer">
                                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                <div className="h-24 bg-black/20 border-2 border-dashed border-white/10 rounded-2xl flex flex-col items-center justify-center gap-2 text-gray-500 hover:border-primary/30 hover:text-primary transition-all">
                                    {image ? (
                                        <img src={image} alt="preview" className="h-full w-full object-cover rounded-2xl" />
                                    ) : (
                                        <>
                                            <Camera size={24} />
                                            <span className="text-[10px] font-bold">صورة القطعة</span>
                                        </>
                                    )}
                                </div>
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !partName}
                        className="w-full py-4 bg-primary text-background-dark rounded-2xl font-black text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:scale-100"
                    >
                        {loading ? 'جاري الإرسال...' : (
                            <>
                                إرسال الطلب للموردين <Send size={16} />
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};
